<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ContactAPI from 'dashboard/api/contacts';

const props = defineProps({
  event: { type: Object, default: null },
  initialDate: { type: Date, default: null },
  initialContact: { type: Object, default: null },
  initialContactId: { type: [Number, String], default: null },
});

const emit = defineEmits(['close', 'saved', 'contact-clicked']);

const store = useStore();

const isEditing = computed(() => props.event && props.event.id);
const isSubmitting = ref(false);

const dateRangeError = computed(() => {
  if (!form.value.starts_at || !form.value.ends_at) return '';

  const start = new Date(form.value.starts_at);
  const end = new Date(form.value.ends_at);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return '';

  if (start > end) {
    return 'A data/hora de início não pode ser maior que a data/hora de fim.';
  }

  return '';
});

const pastCurrentTimeError = ref('');
const originalStartsAt = ref('');
const originalEndsAt = ref('');

function isSameLocalDay(date, reference) {
  return (
    date.getFullYear() === reference.getFullYear() &&
    date.getMonth() === reference.getMonth() &&
    date.getDate() === reference.getDate()
  );
}

function currentMinuteDate() {
  const now = new Date();
  now.setSeconds(0, 0);
  return now;
}

function evaluatePastCurrentTimeError() {
  if (form.value.all_day) return '';

  const now = currentMinuteDate();

  if (form.value.starts_at) {
    const startsAtUnchanged = isEditing.value && form.value.starts_at === originalStartsAt.value;
    if (!startsAtUnchanged) {
      const start = new Date(form.value.starts_at);
      if (!Number.isNaN(start.getTime()) && isSameLocalDay(start, now) && start < now) {
        return 'No dia atual, o horário de início não pode ser retroativo.';
      }
    }
  }

  if (form.value.ends_at) {
    const endsAtUnchanged = isEditing.value && form.value.ends_at === originalEndsAt.value;
    if (!endsAtUnchanged) {
      const end = new Date(form.value.ends_at);
      if (!Number.isNaN(end.getTime()) && isSameLocalDay(end, now) && end < now) {
        return 'No dia atual, o horário de fim não pode ser retroativo.';
      }
    }
  }

  return '';
}

const isSubmitDisabled = computed(() => {
  return (
    isSubmitting.value ||
    !form.value.title ||
    !form.value.starts_at ||
    !!dateRangeError.value ||
    !!pastCurrentTimeError.value
  );
});

// Contact search
const contactQuery = ref('');
const allContacts = ref([]);
const selectedContact = ref(null);
const showContactDropdown = ref(false);
const isLoadingContacts = ref(false);
const contactDropdownContainerRef = ref(null);

const contactResults = computed(() => {
  const query = contactQuery.value.trim().toLowerCase();
  const sortedContacts = [...allContacts.value].sort((a, b) => {
    const aName = (a.name || a.email || '').toLowerCase();
    const bName = (b.name || b.email || '').toLowerCase();
    return aName.localeCompare(bName, 'pt-BR');
  });
  if (!query) return sortedContacts;
  return sortedContacts.filter(contact => {
    const name = (contact.name || '').toLowerCase();
    const email = (contact.email || '').toLowerCase();
    const phone = (contact.phone_number || '').toLowerCase();
    return name.includes(query) || email.includes(query) || phone.includes(query);
  });
});

function formatDateTimeLocal(date) {
  if (!date) return '';
  const d = new Date(date);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatDateOnly(date) {
  if (!date) return '';
  const d = new Date(date);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

const form = ref({
  title: '',
  description: '',
  event_type: 'meeting',
  starts_at: '',
  ends_at: '',
  all_day: false,
  location: '',
  status: 'scheduled',
  contact_id: '',
});

function initializeForm() {
  selectedContact.value = null;
  contactQuery.value = '';
  originalStartsAt.value = '';
  originalEndsAt.value = '';

  form.value = {
    title: '',
    description: '',
    event_type: 'meeting',
    starts_at: '',
    ends_at: '',
    all_day: false,
    location: '',
    status: 'scheduled',
    contact_id: '',
  };

  if (props.event && props.event.id) {
    const isAllDay = props.event.all_day || false;
    form.value = {
      title: props.event.title || '',
      description: props.event.description || '',
      event_type: props.event.event_type || 'meeting',
      starts_at: isAllDay
        ? formatDateOnly(props.event.starts_at)
        : formatDateTimeLocal(props.event.starts_at),
      ends_at: isAllDay
        ? formatDateOnly(props.event.ends_at)
        : formatDateTimeLocal(props.event.ends_at),
      all_day: isAllDay,
      location: props.event.location || '',
      status: props.event.status || 'scheduled',
      contact_id: props.event.contact_id || props.event.contact?.id || '',
    };
    originalStartsAt.value = form.value.starts_at;
    originalEndsAt.value = form.value.ends_at;
    if (props.event.contact) {
      selectedContact.value = props.event.contact;
      contactQuery.value = props.event.contact.name || '';
    }
  } else if (props.initialDate) {
    form.value.starts_at = formatDateTimeLocal(props.initialDate);
    const endDate = new Date(props.initialDate);
    endDate.setHours(endDate.getHours() + 1);
    form.value.ends_at = formatDateTimeLocal(endDate);
  } else {
    const now = new Date();
    const endDate = new Date(now);
    endDate.setMinutes(endDate.getMinutes() + 40);
    form.value.starts_at = formatDateTimeLocal(now);
    form.value.ends_at = formatDateTimeLocal(endDate);
  }

  if (!isEditing.value) {
    const resolvedContactId = Number(
      props.initialContact?.id || props.initialContactId || 0
    );

    if (resolvedContactId) {
      form.value.contact_id = resolvedContactId;
      selectedContact.value =
        props.initialContact && Number(props.initialContact.id) === resolvedContactId
          ? props.initialContact
          : { id: resolvedContactId };
      contactQuery.value =
        props.initialContact?.name ||
        props.initialContact?.email ||
        `#${resolvedContactId}`;
    }
  }
}

watch(
  () => [props.event, props.initialDate, props.initialContact, props.initialContactId],
  () => {
    initializeForm();
  },
  { immediate: true }
);

const eventTypeOptions = [
  { value: 'meeting', label: 'Reunião', icon: 'i-lucide-video' },
  { value: 'follow_up', label: 'Follow-up', icon: 'i-lucide-repeat' },
  { value: 'call', label: 'Ligação', icon: 'i-lucide-phone' },
  { value: 'task', label: 'Tarefa', icon: 'i-lucide-check-square' },
  { value: 'other', label: 'Outro', icon: 'i-lucide-circle' },
];

watch(
  () => form.value.all_day,
  isAllDay => {
    if (isAllDay) {
      if (form.value.starts_at)
        form.value.starts_at = form.value.starts_at.split('T')[0];
      if (form.value.ends_at)
        form.value.ends_at = form.value.ends_at.split('T')[0];
    } else {
      if (form.value.starts_at && !form.value.starts_at.includes('T'))
        form.value.starts_at = `${form.value.starts_at}T09:00`;
      if (form.value.ends_at && !form.value.ends_at.includes('T'))
        form.value.ends_at = `${form.value.ends_at}T10:00`;
    }
  }
);

watch(
  () => [form.value.starts_at, form.value.ends_at, form.value.all_day],
  () => {
    pastCurrentTimeError.value = evaluatePastCurrentTimeError();
  },
  { immediate: true }
);

const statusOptions = [
  { value: 'scheduled', label: 'Agendado' },
  { value: 'completed', label: 'Concluído' },
  { value: 'cancelled', label: 'Cancelado' },
];

function onStartsAtChange() {
  if (form.value.all_day || !form.value.starts_at) return;
  const start = new Date(form.value.starts_at);
  if (Number.isNaN(start.getTime())) return;
  start.setMinutes(start.getMinutes() + 30);
  form.value.ends_at = formatDateTimeLocal(start);
}

function onContactInput() {
  selectedContact.value = null;
  form.value.contact_id = '';
  showContactDropdown.value = contactResults.value.length > 0;
}

async function searchContacts() {
  if (allContacts.value.length > 0) return;
  isLoadingContacts.value = true;
  try {
    let page = 1;
    let hasMore = true;
    const contacts = [];
    while (hasMore) {
      const { data } = await ContactAPI.get(page, 'name');
      const payload = data.payload || [];
      contacts.push(...payload);
      hasMore = payload.length > 0;
      page += 1;
    }
    allContacts.value = contacts;
  } catch {
    allContacts.value = [];
  } finally {
    isLoadingContacts.value = false;
  }
}

async function onContactFocus() {
  await searchContacts();
  showContactDropdown.value = contactResults.value.length > 0;
}

function selectContact(contact) {
  selectedContact.value = contact;
  form.value.contact_id = contact.id;
  contactQuery.value = contact.name || contact.email || `#${contact.id}`;
  showContactDropdown.value = false;
}

function clearContact() {
  selectedContact.value = null;
  form.value.contact_id = '';
  contactQuery.value = '';
}

// Convert datetime-local value to ISO 8601 with timezone for Rails
function toISO(dtStr) {
  if (!dtStr) return dtStr;
  return new Date(dtStr).toISOString();
}

async function handleSubmit() {
  pastCurrentTimeError.value = evaluatePastCurrentTimeError();

  if (
    !form.value.title ||
    !form.value.starts_at ||
    dateRangeError.value ||
    pastCurrentTimeError.value
  )
    return;
  isSubmitting.value = true;

  const payload = { ...form.value };
  // Convert local datetime strings to proper ISO with timezone
  if (payload.starts_at) payload.starts_at = toISO(payload.starts_at);
  if (payload.ends_at) payload.ends_at = toISO(payload.ends_at);
  if (!payload.contact_id) delete payload.contact_id;
  if (!payload.ends_at) delete payload.ends_at;

  try {
    if (isEditing.value) {
      await store.dispatch('crmEvents/update', {
        id: props.event.id,
        ...payload,
      });
    } else {
      await store.dispatch('crmEvents/create', payload);
    }
    emit('saved');
  } catch (error) {
    // handle error
  } finally {
    isSubmitting.value = false;
  }
}

async function handleDelete() {
  if (!isEditing.value) return;
  if (!confirm('Tem certeza que deseja excluir este evento?')) return;
  try {
    await store.dispatch('crmEvents/delete', props.event.id);
    emit('saved');
  } catch (error) {
    // handle error
  }
}
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
    @click.self="emit('close')"
  >
    <div class="bg-n-solid-2 rounded-xl shadow-xl w-full max-w-lg mx-4 overflow-hidden">
      <!-- Header -->
      <div class="flex items-center justify-between px-5 py-3.5 border-b border-n-weak">
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ isEditing ? 'Editar Evento' : 'Novo Evento' }}
        </h3>
        <NextButton slate ghost xs icon="i-lucide-x" @click="emit('close')" />
      </div>

      <!-- Form -->
      <form class="p-5 space-y-4 max-h-[70vh] overflow-y-auto" @submit.prevent="handleSubmit">
        <!-- Title -->
        <div>
          <label class="block text-xs font-medium text-n-slate-11 mb-1">Título *</label>
          <input
            v-model="form.title"
            type="text"
            required
            placeholder="Nome do evento"
            class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-7"
          />
        </div>

        <!-- Event Type -->
        <div>
          <label class="block text-xs font-medium text-n-slate-11 mb-1">Tipo</label>
          <div class="flex gap-2 flex-wrap">
            <button
              v-for="opt in eventTypeOptions"
              :key="opt.value"
              type="button"
              class="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs transition-colors"
              :class="
                form.event_type === opt.value
                  ? 'border-n-blue-7 bg-n-blue-3 text-n-blue-11'
                  : 'border-n-weak text-n-slate-11 hover:bg-n-slate-2'
              "
              @click="form.event_type = opt.value"
            >
              <span :class="opt.icon" class="w-3.5 h-3.5" />
              {{ opt.label }}
            </button>
          </div>
        </div>

        <!-- Date/Time -->
        <div class="flex gap-3">
          <div class="flex-1">
            <label class="block text-xs font-medium text-n-slate-11 mb-1">Início *</label>
            <input
              v-model="form.starts_at"
              :type="form.all_day ? 'date' : 'datetime-local'"
              required
              class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-7"
              @change="onStartsAtChange"
            />
          </div>
          <div class="flex-1">
            <label class="block text-xs font-medium text-n-slate-11 mb-1">Fim</label>
            <input
              v-model="form.ends_at"
              :type="form.all_day ? 'date' : 'datetime-local'"
              :min="form.starts_at || undefined"
              class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-7"
            />
          </div>
        </div>

        <p
          v-if="dateRangeError"
          class="m-0 text-xs text-n-ruby-11"
          data-testid="event-date-range-error"
        >
          {{ dateRangeError }}
        </p>

        <p
          v-if="pastCurrentTimeError"
          class="m-0 text-xs text-n-ruby-11"
          data-testid="event-past-current-time-error"
        >
          {{ pastCurrentTimeError }}
        </p>

        <!-- All day checkbox -->
        <label class="flex items-center gap-2 text-xs text-n-slate-11 cursor-pointer">
          <input
            v-model="form.all_day"
            type="checkbox"
            class="rounded border-n-weak"
          />
          Dia inteiro
        </label>

        <!-- Location -->
        <div>
          <label class="block text-xs font-medium text-n-slate-11 mb-1">Local</label>
          <input
            v-model="form.location"
            type="text"
            placeholder="Local do evento ou link de videoconferência"
            class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-7"
          />
        </div>

        <!-- Contact Search -->
        <div ref="contactDropdownContainerRef" class="relative">
          <label class="block text-xs font-medium text-n-slate-11 mb-1">Contato</label>
          <div class="relative">
            <input
              v-model="contactQuery"
              type="text"
              placeholder="Buscar contato por nome, email ou telefone..."
              autocomplete="off"
              class="w-full rounded-lg border border-n-weak bg-n-solid-1 pl-9 pr-8 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-7"
              @input="onContactInput"
              @focus="onContactFocus"
            />
            <button
              v-if="selectedContact"
              type="button"
              class="absolute right-2 top-1/2 -translate-y-1/2 text-n-slate-9 hover:text-n-slate-12"
              @click="clearContact"
            >
              <span class="i-lucide-x w-3.5 h-3.5" />
            </button>
            <span
              v-if="isLoadingContacts"
              class="i-lucide-loader-2 animate-spin absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-n-slate-9"
            />
          </div>
          <!-- Selected contact pill -->
          <button
            v-if="selectedContact"
            type="button"
            class="mt-1.5 inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-n-blue-3 text-n-blue-11 text-xs hover:bg-n-blue-4 transition-colors"
            title="Abrir conversa com este contato"
            @click="emit('contact-clicked', selectedContact)"
          >
            <span class="i-lucide-user w-3 h-3" />
            <span>{{ selectedContact.name || selectedContact.email }}</span>
            <span class="text-n-blue-8">#{{ selectedContact.id }}</span>
            <span class="i-lucide-message-circle w-3 h-3 ml-0.5" />
          </button>
          <!-- Dropdown -->
          <div
            v-if="showContactDropdown"
            class="absolute z-50 w-full mt-1 bg-n-solid-2 border border-n-weak rounded-lg shadow-lg max-h-48 overflow-y-auto"
          >
            <button
              v-for="contact in contactResults"
              :key="contact.id"
              type="button"
              class="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-n-slate-3 transition-colors"
              @click="selectContact(contact)"
            >
              <span
                class="w-7 h-7 rounded-full bg-n-blue-9 text-white text-[10px] flex items-center justify-center font-medium flex-shrink-0"
              >
                {{ (contact.name || '?').charAt(0).toUpperCase() }}
              </span>
              <div class="flex-1 min-w-0">
                <p class="text-xs font-medium text-n-slate-12 truncate">
                  {{ contact.name || 'Sem nome' }}
                </p>
                <p class="text-[10px] text-n-slate-10 truncate">
                  {{ contact.email || contact.phone_number || `ID: ${contact.id}` }}
                </p>
              </div>
            </button>
          </div>
        </div>

        <!-- Status (edit only) -->
        <div v-if="isEditing">
          <label class="block text-xs font-medium text-n-slate-11 mb-1">Status</label>
          <select
            v-model="form.status"
            class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-7"
          >
            <option v-for="opt in statusOptions" :key="opt.value" :value="opt.value">
              {{ opt.label }}
            </option>
          </select>
        </div>

        <!-- Description -->
        <div>
          <label class="block text-xs font-medium text-n-slate-11 mb-1">Descrição</label>
          <textarea
            v-model="form.description"
            rows="3"
            placeholder="Notas sobre o evento..."
            class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 resize-none focus:outline-none focus:ring-2 focus:ring-n-blue-7"
          />
        </div>

        <!-- Actions -->
        <div class="flex items-center justify-between pt-2">
          <div>
            <NextButton
              v-if="isEditing"
              label="Excluir"
              icon="i-lucide-trash-2"
              ruby
              faded
              sm
              type="button"
              @click="handleDelete"
            />
          </div>
          <div class="flex items-center gap-2">
            <NextButton
              label="Cancelar"
              slate
              faded
              sm
              type="button"
              @click="emit('close')"
            />
            <NextButton
              :label="isEditing ? 'Salvar' : 'Criar'"
              icon="i-lucide-check"
              blue
              sm
              type="submit"
              :is-loading="isSubmitting"
              :disabled="isSubmitDisabled"
            />
          </div>
        </div>
      </form>
    </div>
  </div>
</template>
